import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GameDetails as GameDetailsType, DownloadLinksResponse } from '../types';
import { gameService } from '../services/api';
import { 
  Calendar, 
  Download, 
  ExternalLink, 
  Monitor, 
  HardDrive,
  User,
  Building,
  Star,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';

interface GameDetailsProps {
  game: GameDetailsType;
}

const GameDetails: React.FC<GameDetailsProps> = ({ game }) => {
  const navigate = useNavigate();
  const [downloadLinks, setDownloadLinks] = useState<DownloadLinksResponse | null>(null);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdModal, setShowAdModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [hasSeenAd, setHasSeenAd] = useState(false);

  useEffect(() => {
    const adSeen = sessionStorage.getItem('tucracks_ad_seen');
    if (adSeen) {
      setHasSeenAd(true);
    }
  }, []);

  const handleDownloadClick = async () => {
    // Toujours afficher la pub avant de télécharger
    setShowAdModal(true);
  };

  const fetchDownloadLinks = async () => {
    const firstLink = game.download_links![0];
    setLoadingLinks(true);
    setError(null);
    
    try {
      const links = await gameService.getDownloadLinks(firstLink.url);
      setDownloadLinks(links);
    } catch (err) {
      setError('Erreur lors du chargement des liens de téléchargement');
      console.error('Download error:', err);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleWatchAd = () => {
    window.open('https://www.effectivegatecpm.com/d07def2p6?key=a1d6e2717709d1a6c103184bbcb12246', '_blank');
    sessionStorage.setItem('tucracks_ad_seen', 'true');
    setHasSeenAd(true);
    setShowAdModal(false);
    setShowThankYouModal(true);
    
    // Récupérer les liens après avoir vu la pub
    setTimeout(() => {
      fetchDownloadLinks();
    }, 1000);
  };

  const handleCancelAd = () => {
    setShowAdModal(false);
  };

  const handleThankYouContinue = () => {
    setShowThankYouModal(false);
  };

  const formatSystemRequirements = (requirements: Record<string, any>) => {
    if (!requirements) return null;
    
    return (
      <div className="space-y-6">
        {Object.entries(requirements).map(([key, value]) => (
          <div key={key} className="relative pl-6 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-gradient-to-b before:from-primary-500 before:to-primary-600 before:rounded-full">
            <h4 className="font-bold text-lg text-gray-900 dark:text-white capitalize mb-3">
              {key.replace(/_/g, ' ')}
            </h4>
            {Array.isArray(value) ? (
              <ul className="space-y-2">
                {value.map((item, index) => (
                  <li key={index} className="flex items-start text-gray-700 dark:text-gray-300">
                    <span className="inline-block w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-700 dark:text-gray-300">{value}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return 'Lien';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header avec effet glassmorphism */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <div className="lg:col-span-1">
          {game.image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 to-purple-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
              <img
                src={game.image_url}
                alt={game.title}
                className="relative w-full rounded-2xl shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-300"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/placeholder-game.jpg';
                }}
              />
            </motion.div>
          )}
        </div>
        
        <div className="lg:col-span-2 space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 mb-3 leading-tight">
              {game.title}
            </h1>
            
            {game.release_name && (
              <p className="text-xl text-gray-600 dark:text-gray-400 font-medium">
                {game.release_name}
              </p>
            )}
          </motion.div>

          {/* Info Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {game.developer && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="flex items-center bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800"
              >
                <div className="bg-blue-600 p-2 rounded-lg mr-3">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">Développeur</p>
                  <p className="text-gray-900 dark:text-white font-bold">{game.developer}</p>
                </div>
              </motion.div>
            )}
            
            {game.publisher && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15 }}
                className="flex items-center bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-4 rounded-xl border border-purple-200 dark:border-purple-800"
              >
                <div className="bg-purple-600 p-2 rounded-lg mr-3">
                  <Building className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">Éditeur</p>
                  <p className="text-gray-900 dark:text-white font-bold">{game.publisher}</p>
                </div>
              </motion.div>
            )}
            
            {game.release_date && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-center bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-4 rounded-xl border border-green-200 dark:border-green-800"
              >
                <div className="bg-green-600 p-2 rounded-lg mr-3">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">Date de sortie</p>
                  <p className="text-gray-900 dark:text-white font-bold">{game.release_date}</p>
                </div>
              </motion.div>
            )}
            
            {game.genre && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.25 }}
                className="flex items-center bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-4 rounded-xl border border-orange-200 dark:border-orange-800"
              >
                <div className="bg-orange-600 p-2 rounded-lg mr-3">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold mb-1">Genre</p>
                  <div className="flex flex-wrap gap-2">
                    {game.genre.split(',').map((genre, index) => (
                      <button
                        key={index}
                        onClick={() => navigate(`/genre/${genre.trim().toLowerCase().replace(/\s+/g, '-')}`)}
                        className="px-2 py-1 bg-orange-600 text-white text-xs font-semibold rounded-lg hover:bg-orange-700 transition-colors"
                      >
                        {genre.trim()}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Size Info avec design moderne */}
          {(game.repack_size || game.original_size) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap gap-3"
            >
              {game.repack_size && (
                <div className="flex items-center bg-gradient-to-r from-emerald-500 to-green-600 text-white px-5 py-3 rounded-xl shadow-lg">
                  <HardDrive className="w-5 h-5 mr-2" />
                  <div>
                    <p className="text-xs font-semibold opacity-90">Repack</p>
                    <p className="text-lg font-bold">{game.repack_size}</p>
                  </div>
                </div>
              )}
              {game.original_size && (
                <div className="flex items-center bg-gradient-to-r from-sky-500 to-blue-600 text-white px-5 py-3 rounded-xl shadow-lg">
                  <Monitor className="w-5 h-5 mr-2" />
                  <div>
                    <p className="text-xs font-semibold opacity-90">Original</p>
                    <p className="text-lg font-bold">{game.original_size}</p>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Action Buttons - Design premium */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="flex flex-wrap gap-4"
          >
            {game.steam_url && (
              <a
                href={game.steam_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-3 text-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
              >
                <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                <ExternalLink className="w-5 h-5 mr-2 relative z-10" />
                <span className="relative z-10 font-bold">Voir sur Steam</span>
              </a>
            )}

            {game.download_links && game.download_links.length > 0 && (
              <button
                onClick={handleDownloadClick}
                disabled={loadingLinks}
                className="group relative inline-flex items-center overflow-hidden rounded-xl bg-gradient-to-r from-emerald-600 via-green-600 to-emerald-700 px-6 py-3 text-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity"></span>
                {loadingLinks ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2 relative z-10" />
                    <span className="relative z-10 font-bold">Télécharger maintenant</span>
                    <Star className="w-4 h-4 ml-2 relative z-10" />
                  </>
                )}
              </button>
            )}
          </motion.div>
        </div>
      </div>

      {/* Modal Publicité - Design ultra moderne */}
      <AnimatePresence>
        {showAdModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl relative"
            >
              {/* Header avec gradient animé */}
              <div className="relative h-32 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 overflow-hidden">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
                <div className="absolute top-4 right-4">
                  <button
                    onClick={handleCancelAd}
                    className="bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
                <div className="relative h-full flex items-center justify-center">
                  <div className="text-center">
                    <Star className="w-12 h-12 text-white mx-auto mb-2 animate-pulse" />
                    <h3 className="text-2xl font-black text-white">Accès téléchargement</h3>
                  </div>
                </div>
              </div>

              <div className="p-8 space-y-6">
                {/* Message principal */}
                <div className="text-center space-y-3">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-2xl mb-2">
                    <AlertCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">
                    Un clic pour débloquer
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    Soutenez <span className="font-bold text-primary-600 dark:text-primary-400">TUCRACKS</span> en regardant une courte publicité. C'est gratuit et ça prend 2 secondes !
                  </p>
                </div>

                {/* Warning card */}
                <div className="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border-2 border-yellow-400 dark:border-yellow-600 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <div className="bg-yellow-400 dark:bg-yellow-600 p-2 rounded-xl flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-yellow-900 dark:text-yellow-200 mb-1">
                        Important !
                      </p>
                      <p className="text-sm text-yellow-800 dark:text-yellow-300">
                        Ne cliquez que sur la publicité qui s'ouvre. Ne téléchargez rien et ne scannez rien !
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleCancelAd}
                    className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-6 py-4 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-all font-bold"
                  >
                    Retour
                  </button>
                  <button
                    onClick={handleWatchAd}
                    className="flex-1 relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all group"
                  >
                    <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></span>
                    <span className="relative flex items-center justify-center gap-2">
                      <Star className="w-5 h-5" />
                      Regarder une publicité !
                    </span>
                  </button>
                </div>

                <p className="text-center text-xs text-gray-500 dark:text-gray-500">
                  Vous ne verrez cette demande qu'une fois par session
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal Remerciement - Design épuré et moderne */}
      <AnimatePresence>
        {showThankYouModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[10000]"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, rotate: -5 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 15 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl relative"
            >
              {/* Confetti background effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 via-emerald-400/10 to-teal-400/10"></div>
              
              <div className="relative p-8 text-center space-y-6">
                {/* Success icon animé */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", damping: 10 }}
                  className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full shadow-xl mx-auto"
                >
                  <CheckCircle className="w-12 h-12 text-white" />
                </motion.div>

                <div className="space-y-3">
                  <h3 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600">
                    Merci infiniment !
                  </h3>
                  <p className="text-lg text-gray-700 dark:text-gray-300">
                    Grâce à vous, <span className="font-bold text-primary-600 dark:text-primary-400">TUCRACKS</span> reste gratuit pour tous 🎮
                  </p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-5">
                  <p className="text-green-800 dark:text-green-200 font-semibold">
                    ✨ Vos liens de téléchargement sont prêts !
                  </p>
                </div>

                <button
                  onClick={handleThankYouContinue}
                  className="w-full relative overflow-hidden bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all group"
                >
                  <span className="absolute inset-0 bg-white/20 translate-x-full group-hover:translate-x-0 transition-transform"></span>
                  <span className="relative flex items-center justify-center gap-2">
                    Voir les liens
                    <Download className="w-5 h-5" />
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Description */}
      {game.description && game.description.trim() !== "MINIMUM:" && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full"></span>
            Description
          </h2>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-8 rounded-2xl border border-gray-200 dark:border-gray-700">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
              {game.description}
            </p>
          </div>
        </motion.div>
      )}

      {/* System Requirements */}
      {game.system_requirements && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></span>
            Configuration requise
          </h2>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-8 rounded-2xl border border-purple-200 dark:border-purple-800">
            {formatSystemRequirements(game.system_requirements)}
          </div>
        </motion.div>
      )}

      {/* Installation Guide */}
      {game.installation_guide && game.installation_guide.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-blue-500 to-blue-600 rounded-full"></span>
            Guide d'installation
          </h2>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-8 rounded-2xl border border-blue-200 dark:border-blue-800">
            <div className="space-y-4">
              {game.installation_guide.map((step, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white font-bold rounded-lg flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* General Notes */}
      {game.general_notes && game.general_notes.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-yellow-500 to-yellow-600 rounded-full"></span>
            Notes importantes
          </h2>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 p-8 rounded-2xl border border-yellow-200 dark:border-yellow-800">
            <div className="space-y-3">
              {game.general_notes.map((note, index) => (
                <div key={index} className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 dark:text-gray-300">{note}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* Download Links Dialog - Design premium */}
      <AnimatePresence>
        {downloadLinks && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
            onClick={() => setDownloadLinks(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-3xl w-full max-h-[85vh] overflow-hidden shadow-2xl relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header avec gradient */}
              <div className="relative bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 p-6">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
                      <Download className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-black text-white">
                        Liens de téléchargement
                      </h3>
                      <p className="text-white/80 text-sm font-medium">
                        {downloadLinks.total_links} liens disponibles
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setDownloadLinks(null)}
                    className="bg-white/20 backdrop-blur-sm hover:bg-white/30 p-2 rounded-xl transition-colors"
                  >
                    <X className="w-6 h-6 text-white" />
                  </button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 bg-red-100 dark:bg-red-900/30 border-2 border-red-500 text-red-800 dark:text-red-200 px-5 py-4 rounded-2xl mb-6"
                  >
                    <AlertCircle className="w-6 h-6 flex-shrink-0" />
                    <span className="font-semibold">{error}</span>
                  </motion.div>
                )}
                
                <div className="space-y-3">
                  {downloadLinks.links.map((link, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="group relative bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border-2 border-gray-200 dark:border-gray-700 hover:border-green-500 dark:hover:border-green-500 p-5 rounded-2xl transition-all duration-300"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 p-3 rounded-xl group-hover:scale-110 transition-transform">
                            <Download className="w-5 h-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-900 dark:text-white font-bold text-lg truncate">
                              {getDomainFromUrl(link.url)}
                            </p>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                              Miroir #{index + 1}
                            </p>
                          </div>
                        </div>
                        <motion.a
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all whitespace-nowrap"
                        >
                          <Download className="w-5 h-5" />
                          Télécharger
                        </motion.a>
                      </div>
                      {/* Effet de brillance au survol */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 rounded-2xl"></div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screenshots */}
      {game.screenshots && game.screenshots.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-pink-500 to-pink-600 rounded-full"></span>
            Captures d'écran
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {game.screenshots.map((screenshot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => window.open(screenshot, '_blank')}
              >
                <img
                  src={screenshot}
                  alt={`Screenshot ${index + 1}`}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-white font-bold bg-white/20 backdrop-blur-sm px-4 py-2 rounded-xl">
                    Agrandir
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Trailer */}
      {game.trailer_url && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-red-500 to-red-600 rounded-full"></span>
            Bande-annonce
          </h2>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 rounded-2xl border border-gray-200 dark:border-gray-700">
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
              {game.trailer_url.includes('youtube.com') ? (
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src={game.trailer_url.replace('watch?v=', 'embed/')}
                  title="Trailer du jeu"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  className="absolute top-0 left-0 w-full h-full"
                  controls
                  preload="metadata"
                  poster={game.image_url}
                >
                  <source src={game.trailer_url} type="video/mp4" />
                  <source src={game.trailer_url} type="video/webm" />
                  <source src={game.trailer_url} type="video/ogg" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Related Games */}
      {game.related_games && game.related_games.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mb-12"
        >
          <h2 className="text-3xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            <span className="w-2 h-8 bg-gradient-to-b from-indigo-500 to-indigo-600 rounded-full"></span>
            Jeux similaires
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {game.related_games.map((relatedGame, index) => {
              const handleRelatedGameClick = () => {
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
                
                const gamePath = relatedGame.url.replace('https://game3rb.com/', '').replace(/\/$/, '');
                navigate(`/game/${gamePath}`);
              };

              return (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1 + index * 0.1 }}
                  className="group relative bg-white dark:bg-gray-900 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden border-2 border-transparent hover:border-primary-500"
                  onClick={handleRelatedGameClick}
                >
                  {relatedGame.image_url && (
                    <div className="relative overflow-hidden">
                      <img
                        src={relatedGame.image_url}
                        alt={relatedGame.title}
                        className="w-full h-56 object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = '/placeholder-game.jpg';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  )}
                  <div className="p-5">
                    <h4 className="font-bold text-lg text-gray-900 dark:text-white line-clamp-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {relatedGame.title}
                    </h4>
                    <div className="mt-3 flex items-center text-primary-600 dark:text-primary-400 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>Voir le jeu</span>
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default GameDetails;